/**
 * FormBuilder - Pure HTML form generator for MOJO framework
 * Generates form HTML from field definitions with Bootstrap 5 grid layout support
 * NO event handling, lifecycle, or mounting - pure HTML generation only
 */

import Mustache from '@core/utils/mustache.js';
import MOJOUtils from '@core/utils/MOJOUtils.js';
import { FormPlugins } from '@core/forms/FormPlugins.js';
import TabView from '@core/views/navigation/TabView.js';

class FormBuilder {
    constructor(config = {}) {
      this.fields = config.fields || [];
      this.structureOnly = config.structureOnly || false; // New option

      // Convert cols to columns for all fields
      this.fields.forEach(field => {
        if (field.cols && !field.columns) {
          field.columns = field.cols;
          delete field.cols;
        } else if (!field.columns) {
          field.columns = 12;
        }

        // Handle nested fields in groups
        if (field.type === 'group' && field.fields) {
          field.fields.forEach(groupField => {
            if (groupField.cols && !groupField.columns) {
              groupField.columns = groupField.cols;
              delete groupField.cols;
            } else if (!groupField.columns) {
              groupField.columns = 12;
            }
          });
        }
      });

      this.options = {
        formClass: 'needs-validation',
        formMethod: 'POST',
        formAction: '',
        groupClass: 'row mb-3',
        fieldWrapper: '',
        labelClass: 'form-label',
        inputClass: 'form-control',
        errorClass: 'invalid-feedback',
        helpClass: 'form-text',
        submitButton: false,
        resetButton: false,
        ...config.options
      };
      this.buttons = config.buttons || [];
      this.data = config.data || {};
      this.errors = config.errors || {};
      this.initializeTemplates();
    }

  /**
   * Initialize field templates
   */
  initializeTemplates() {
    FormPlugins.onFormBuilderInit?.(this);
    this.templates = {
      input: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          {{#showCopy}}
          <div class="input-group">
            <input type="{{type}}" id="{{fieldId}}" name="{{name}}"
                   class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                   value="{{fieldValue}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                   {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                   {{#readonly}}readonly{{/readonly}} {{{attrs}}}>
            <button class="btn btn-outline-secondary" type="button" data-action="copy-to-clipboard" data-target="{{fieldId}}" title="Copy to clipboard">
              <i class="bi bi-clipboard"></i>
            </button>
          </div>
          {{/showCopy}}
          {{^showCopy}}
          <input type="{{type}}" id="{{fieldId}}" name="{{name}}"
                 class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                 value="{{fieldValue}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                 {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                 {{#readonly}}readonly{{/readonly}} {{{attrs}}}>
          {{/showCopy}}
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      password: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          <div class="input-group">
            <input type="password" id="{{fieldId}}" name="{{name}}"
                   class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                   value="{{fieldValue}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                   {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                   {{#readonly}}readonly{{/readonly}}
                   data-field-type="password" {{{attrs}}}>
            {{#showToggle}}
            <button type="button" class="btn btn-outline-secondary"
                    data-action="toggle-password"
                    data-target="{{fieldId}}"
                    aria-label="Show password" aria-pressed="false">
              <i class="bi bi-eye"></i>
            </button>
            {{/showToggle}}
          </div>
          {{#strengthMeter}}
          <div class="mt-2">
            <div class="progress" style="height: 4px;">
              <div class="progress-bar bg-secondary" role="progressbar"
                   style="width: 0%;" aria-valuenow="0" aria-valuemin="0" aria-valuemax="100"
                   id="{{fieldId}}_strength_bar"></div>
            </div>
            <small class="{{helpClass}}" id="{{fieldId}}_strength_text">Strength</small>
          </div>
          {{/strengthMeter}}
          {{#capsLockWarning}}
          <div class="{{helpClass}} text-warning d-none" id="{{fieldId}}_caps_warning">
            Caps Lock is on
          </div>
          {{/capsLockWarning}}
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,



      textarea: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          {{#showCopy}}
          <div class="position-relative">
            <textarea id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                      rows="{{rows}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                      {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                      {{#readonly}}readonly{{/readonly}} {{{attrs}}}>{{fieldValue}}</textarea>
            <button class="btn btn-sm btn-outline-secondary position-absolute top-0 end-0 m-2" type="button" data-action="copy-to-clipboard" data-target="{{fieldId}}" title="Copy to clipboard">
              <i class="bi bi-clipboard"></i>
            </button>
          </div>
          {{/showCopy}}
          {{^showCopy}}
          <textarea id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                    rows="{{rows}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                    {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                    {{#readonly}}readonly{{/readonly}} {{{attrs}}}>{{fieldValue}}</textarea>
          {{/showCopy}}
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      htmlpreview: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          <div class="position-relative">
            <textarea id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                      rows="{{rows}}" {{#placeholder}}placeholder="{{placeholder}}"{{/placeholder}}
                      {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                      {{#readonly}}readonly{{/readonly}}
                      data-field-type="htmlpreview" {{{attrs}}}>{{fieldValue}}</textarea>
            <div class="position-absolute d-flex gap-1" style="top: 8px; right: 8px; z-index: 10;">
              {{#showCopy}}
              <button type="button" class="btn btn-sm btn-outline-secondary"
                      data-action="copy-to-clipboard"
                      data-target="{{fieldId}}"
                      title="Copy to clipboard"
                      aria-label="Copy to clipboard">
                <i class="bi bi-clipboard"></i>
              </button>
              {{/showCopy}}
              <button type="button" class="btn btn-sm btn-outline-secondary"
                      data-action="preview-html"
                      data-target="{{fieldId}}"
                      title="Preview HTML"
                      aria-label="Preview HTML">
                <i class="bi bi-eye"></i>
              </button>
            </div>
          </div>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      select: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          {{#searchInput}}{{{searchInput}}}{{/searchInput}}
          <select id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                  {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                  {{#multiple}}multiple{{/multiple}} {{{attrs}}}>
            {{{optionsHTML}}}
          </select>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      checkbox: `
        <div class="mojo-form-control">
          <div class="form-check {{fieldClass}}">
            <input type="checkbox" id="{{fieldId}}" name="{{name}}"
                   class="form-check-input{{#error}} is-invalid{{/error}}" value="{{value}}"
                   {{#checked}}checked{{/checked}} {{#required}}required{{/required}}
                     {{#disabled}}disabled{{/disabled}} {{{attrs}}}>
              <label class="form-check-label" for="{{fieldId}}">{{label}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}</label>
            </div>
            {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
            {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
          </div>
      `,

      switch: `
        <div class="mojo-form-control">
          <div class="form-check form-switch {{sizeClass}} {{fieldClass}}">
            <input type="checkbox" id="{{fieldId}}" name="{{name}}" role="switch"
                   class="form-check-input{{#error}} is-invalid{{/error}}" value="{{value}}"
                   {{#checked}}checked{{/checked}} {{#required}}required{{/required}}
                   {{#disabled}}disabled{{/disabled}} data-change-action="toggle-switch"
                   data-field="{{name}}" {{{attrs}}}>
            <label class="form-check-label" for="{{fieldId}}">{{label}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}</label>
          </div>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      image: `
        <div class="mojo-form-control" style="display: flex; flex-direction: column; align-items: center;">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          <div class="image-field-container {{containerClass}}" id="{{dropZoneId}}" style="width: {{width}}px; height: {{height}}px;">
            <input type="file" id="{{fieldId}}" name="{{name}}" class="{{inputClass}} d-none{{#error}} is-invalid{{/error}}"
                   accept="{{accept}}" {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                   data-change-action="image-selected" data-field="{{name}}" {{{attrs}}}>
            <div class="image-drop-zone{{#allowDrop}} droppable{{/allowDrop}}" data-action="click-image-upload"
                 data-field-id="{{fieldId}}" data-field="{{name}}" style="width: 100%; height: 100%; cursor: {{cursor}};">
              {{#imageUrl}}
              <img id="{{previewId}}" src="{{imageUrl}}" alt="Preview" class="img-thumbnail w-100 h-100" style="object-fit: cover;">
              {{#showRemove}}
              <button type="button" class="btn btn-sm btn-danger position-absolute top-0 end-0 m-1"
                      data-action="remove-image" data-field-id="{{fieldId}}" data-field="{{name}}" style="opacity: 0.8;">
                <i class="bi bi-x"></i>
              </button>
              {{/showRemove}}
              {{/imageUrl}}
              {{^imageUrl}}
              <div class="d-flex flex-column align-items-center justify-content-center h-100 text-muted border border-2 border-dashed">
                <i class="bi bi-image fs-1 mb-2"></i>
                <small class="text-center px-2">{{placeholderText}}</small>
              </div>
              {{/imageUrl}}
            </div>
          </div>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      range: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}: <span id="{{fieldId}}_value">{{fieldValue}}</span>
          </label>
          {{/label}}
          <input type="range" id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                 min="{{min}}" max="{{max}}" step="{{step}}" value="{{fieldValue}}" {{#disabled}}disabled{{/disabled}}
                 data-change-action="range-changed" data-target="{{fieldId}}_value" {{{attrs}}}>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      file: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          <input type="file" id="{{fieldId}}" name="{{name}}" class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                 accept="{{accept}}" {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                 {{#multiple}}multiple{{/multiple}} data-change-action="file-selected" {{{attrs}}}>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      radio: `
        <div class="mojo-form-control">
          {{#label}}<div class="{{labelClass}}">{{label}}{{#required}}<span class="text-danger">*</span>{{/required}}</div>{{/label}}
          {{#options}}
          <div class="form-check">
            <input type="radio" id="{{fieldId}}_{{value}}" name="{{name}}" value="{{value}}"
                   class="form-check-input{{#error}} is-invalid{{/error}}" {{#checked}}checked{{/checked}}
                   {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                   {{{attrs}}}>
            <label class="form-check-label" for="{{fieldId}}_{{value}}">{{text}}</label>
          </div>
          {{/options}}
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `,

      button: `
        <button type="button" {{#name}}name="{{name}}"{{/name}} class="btn {{fieldClass}}"
                {{#action}}data-action="{{action}}"{{/action}} {{#disabled}}disabled{{/disabled}} {{{attrs}}}>
          {{label}}
        </button>
      `,

      divider: `
        <hr class="{{dividerClass}}">
      `,

      html: `
        <div class="form-html {{fieldClass}}">{{{html}}}</div>
      `,

      header: `
        <h{{level}} {{#id}}id="{{id}}"{{/id}} class="text-primary mb-3 {{fieldClass}}">{{text}}</h{{level}}>
      `,

      hidden: `
        <input type="hidden" id="{{fieldId}}" name="{{name}}" value="{{fieldValue}}">
      `,

      checklistdropdown: `
        <div class="dropdown">
          <button class="{{buttonClass}}" type="button" data-bs-toggle="dropdown">
            <i class="{{buttonIcon}} me-1"></i> {{buttonText}}
          </button>
          <div class="{{dropdownClass}}" style="min-width: {{minWidth}};">
            {{#options}}
            <div class="form-check">
              <input class="form-check-input" type="checkbox"
                     value="{{value}}"
                     id="{{id}}"
                     {{#checked}}checked{{/checked}}
                     data-change-action="update-checklist"
                     data-field="{{fieldName}}">
              <label class="form-check-label" for="{{id}}">
                {{label}}
              </label>
            </div>
            {{/options}}
            <hr class="my-2">
            <button class="btn btn-sm btn-primary w-100" data-action="apply-filter">
              Apply
            </button>
          </div>
        </div>
      `,

      buttongroup: `
        <div class="btn-group btn-group-{{size}}" role="group">
          {{#options}}
          <button type="button" class="{{buttonClass}} {{#active}}active{{/active}}"
                  {{^action}}
                  data-action="select-button-option"
                  {{/action}}
                  {{#action}}
                  data-action='{{action}}'
                  {{/action}}
                  data-field="{{fieldName}}"
                  data-value="{{value}}">
            {{#icon}}<i class="{{icon}} me-1"></i> {{/icon}} {{label}}
          </button>
          {{/options}}
        </div>
      `,

      toolbarform: `
        <div class="mojo-toolbar-form">
          <div class="row g-2 align-items-center">
            {{#fields}}
            <div class="{{containerClass}}">
              {{#label}}<label class="form-label-sm mb-1">{{label}}</label>{{/label}}
              {{{fieldHtml}}}
            </div>
            {{/fields}}
          </div>
        </div>
      `,

      color: `
        <div class="mojo-form-control">
          {{#label}}
          <label for="{{fieldId}}" class="{{labelClass}}">
            {{label}}{{#required}}<span class="text-danger">*</span>{{/required}}{{#tooltip}} <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="{{tooltip}}" style="font-size: 0.75rem; cursor: help;"></i>{{/tooltip}}
          </label>
          {{/label}}
          <div class="d-flex align-items-center gap-2">
            <input type="color" id="{{fieldId}}" name="{{name}}"
                   class="{{inputClass}}{{#error}} is-invalid{{/error}}"
                   value="{{fieldValue}}"
                   {{#required}}required{{/required}} {{#disabled}}disabled{{/disabled}}
                   {{#readonly}}readonly{{/readonly}} {{{attrs}}}>
            <button type="button" class="btn-sm text-muted border-0 bg-transparent p-1"
                    data-action="clear-color" data-field="{{name}}"
                    title="Clear color">
              <i class="bi bi-x fs-5"></i>
            </button>
          </div>
          {{#help}}<div class="{{helpClass}}">{{help}}</div>{{/help}}
          {{#error}}<div class="{{errorClass}}">{{error}}</div>{{/error}}
        </div>
      `
    };
  }

  /**
   * Generate complete form HTML
   * @returns {string} Complete form HTML
   */
  buildFormHTML() {
    const fieldsHTML = this.buildFieldsHTML();
    const buttonsHTML = this.buildButtonsHTML();

    return `
      <form class="${this.options.formClass}" novalidate>
        ${fieldsHTML}
        ${buttonsHTML}
      </form>
    `;
  }

  /**
   * Generate HTML for all fields
   * @returns {string} Fields HTML
   */
  /**
   * Check if field should use auto-sizing columns (class="col")
   * @param {object} field - Field configuration
   * @returns {boolean}
   */
  isAutoSizingField(field) {
    return !field.columns || field.columns === 'auto' || field.columns === '';
  }

  buildFieldsHTML() {
    const result = [];
    let i = 0;

    while (i < this.fields.length) {
      const field = this.fields[i];
      field.columns = field.columns || field.cols;

      if (field.type === 'group') {
        // Collect consecutive groups that should be in a row
        const groupsInRow = [field];
        let totalColumns = field.columns || 12;

        // Handle responsive columns - use md breakpoint for calculation, fallback to largest available
        if (typeof totalColumns === 'object' && totalColumns !== null) {
          totalColumns = totalColumns.md || totalColumns.sm || totalColumns.xs || 12;
        }

        let j = i + 1;

        // Look ahead for more groups that can fit in the same row
        while (j < this.fields.length &&
               this.fields[j].type === 'group' &&
               totalColumns < 12) {
          const nextGroup = this.fields[j];
          let nextColumns = nextGroup.columns || 12;

          // Handle responsive columns - use md breakpoint for calculation, fallback to largest available
          if (typeof nextColumns === 'object' && nextColumns !== null) {
            nextColumns = nextColumns.md || nextColumns.sm || nextColumns.xs || 12;
          }

          if (totalColumns + nextColumns <= 12) {
            groupsInRow.push(nextGroup);
            totalColumns += nextColumns;
            j++;
          } else {
            break;
          }
        }

        // If we have multiple groups or a single group with specific columns, wrap in row
        let shouldWrap = groupsInRow.length > 1;

        // Check if single group has specific columns (not full width)
        if (groupsInRow.length === 1 && field.columns) {
          let cols = field.columns;
          if (typeof cols === 'object' && cols !== null) {
            cols = cols.md || cols.sm || cols.xs || 12;
          }
          shouldWrap = shouldWrap || cols < 12;
        }

        if (shouldWrap) {
          const groupsHTML = groupsInRow.map(group => this.buildGroupHTML(group)).join('');
          result.push(`<div class="row">${groupsHTML}</div>`);
        } else {
          result.push(this.buildGroupHTML(field));
        }

        i = j;
      } else if (field.columns && field.columns < 12) {
        // Collect consecutive fields with columns that should be in a row
        const fieldsInRow = [field];
        let totalColumns = field.columns || 12;

        // Handle responsive columns - use md breakpoint for calculation, fallback to largest available
        if (typeof totalColumns === 'object' && totalColumns !== null) {
          totalColumns = totalColumns.md || totalColumns.sm || totalColumns.xs || 12;
        }

        let j = i + 1;

        // Look ahead for more fields that can fit in the same row
        while (j < this.fields.length &&
               this.fields[j].columns &&
               totalColumns < 12) {
          const nextField = this.fields[j];
          let nextColumns = nextField.columns || 12;

          // Handle responsive columns - use md breakpoint for calculation, fallback to largest available
          if (typeof nextColumns === 'object' && nextColumns !== null) {
            nextColumns = nextColumns.md || nextColumns.sm || nextColumns.xs || 12;
          }

          if (totalColumns + nextColumns <= 12) {
            fieldsInRow.push(nextField);
            totalColumns += nextColumns;
            j++;
          } else {
            break;
          }
        }

        // Wrap fields in a row
        const fieldsHTML = fieldsInRow.map(f => this.buildFieldHTML(f)).join('');
        result.push(`<div class="row">${fieldsHTML}</div>`);

        i = j;
      } else if (this.isAutoSizingField(field)) {
        // Collect consecutive auto-sizing fields that should be in a row
        const fieldsInRow = [field];
        let j = i + 1;

        // Look ahead for more auto-sizing fields
        while (j < this.fields.length) {
          const nextField = this.fields[j];
          if (this.isAutoSizingField(nextField)) {
            fieldsInRow.push(nextField);
            j++;
          } else {
            break;
          }
        }

        // If we have multiple auto-sizing fields, wrap them in a row
        if (fieldsInRow.length > 1) {
          const fieldsHTML = fieldsInRow.map(f => this.buildFieldHTML(f)).join('');
          result.push(`<div class="row">${fieldsHTML}</div>`);
        } else {
          // Single auto-sizing field, still wrap in row for consistency
          result.push(`<div class="row">${this.buildFieldHTML(field)}</div>`);
        }

        i = j;
      } else {
        result.push(this.buildFieldHTML(field));
        i++;
      }
    }

    return result.join('');
  }

  /**
   * Generate HTML for a field group with column layout
   * @param {Object} group - Group configuration
   * @returns {string} Group HTML
   */
  buildGroupHTML(group) {
    const {
      columns = 12,
      title,
      fields = [],
      class: groupClass = '',
      titleClass = 'h6 mb-3',
      responsive = {}
    } = group;

    // Build responsive column classes
    let colClasses = [];

    // Handle columns as object (responsive) or number (legacy)
    if (typeof columns === 'object' && columns !== null) {
      // New responsive syntax: columns: { xs: 12, sm: 6, md: 4, lg: 3 }
      if (columns.xs) colClasses.push(`col-${columns.xs}`);
      if (columns.sm) colClasses.push(`col-sm-${columns.sm}`);
      if (columns.md) colClasses.push(`col-md-${columns.md}`);
      if (columns.lg) colClasses.push(`col-lg-${columns.lg}`);
      if (columns.xl) colClasses.push(`col-xl-${columns.xl}`);
      if (columns.xxl) colClasses.push(`col-xxl-${columns.xxl}`);

      // If no md is specified but xs/sm are, use xs or sm as fallback for md
      if (!columns.md && (columns.xs || columns.sm)) {
        const fallback = columns.sm || columns.xs;
        colClasses.push(`col-md-${fallback}`);
      }

      // If no breakpoints specified at all, default to col-md-12
      if (colClasses.length === 0) {
        colClasses.push('col-md-12');
      }
    } else {
      // Legacy syntax: columns: 6
      colClasses.push(`col-md-${columns}`);
    }

    // Support legacy responsive property (for backward compatibility)
    if (responsive.xs) colClasses.push(`col-${responsive.xs}`);
    if (responsive.sm) colClasses.push(`col-sm-${responsive.sm}`);
    if (responsive.lg) colClasses.push(`col-lg-${responsive.lg}`);
    if (responsive.xl) colClasses.push(`col-xl-${responsive.xl}`);

    const colClass = colClasses.join(' ');
    const fieldsHTML = fields.map(field => {
      if (field.type === 'group') {
        // Nested groups (limited depth)
        return this.buildGroupHTML(field);
      }
      return this.buildFieldHTML(field);
    }).join('');

    return `
      <div class="${colClass}">
        <div class="mojo-form-group ${groupClass}">
          ${title ? `<div class="${titleClass}">${this.escapeHtml(title)}</div>` : ''}
          <div class="row">
            ${fieldsHTML}
          </div>
        </div>
      </div>
    `;
  }

  /**
   * Generate HTML for a single field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  buildFieldHTML(field) {
    const { type, columns, class: fieldClass = '' } = field;

    let fieldHTML = '';
    // If a plugin registered a custom renderer for this field type, use it
    const customRenderer = (FormPlugins && typeof FormPlugins.getRenderer === 'function')
      ? FormPlugins.getRenderer(type)
      : null;
    if (typeof customRenderer === 'function') {
      try {
        const custom = customRenderer(this, field);
        if (custom !== undefined && custom !== null) {
          fieldHTML = String(custom);
        }
      } catch (err) {
        console.error('FormPlugins custom renderer error:', err);
      }
    }
    if (!fieldHTML) {
      switch (type) {
      case 'text':
        fieldHTML = this.renderTextField(field);
        break;
      case 'email':
        fieldHTML = this.renderEmailField(field);
        break;
      case 'password':
        fieldHTML = this.renderPasswordField(field);
        break;
      case 'number':
        fieldHTML = this.renderNumberField(field);
        break;
      case 'tel':
        fieldHTML = this.renderTelField(field);
        break;
      case 'url':
        fieldHTML = this.renderUrlField(field);
        break;
      case 'search':
        fieldHTML = this.renderSearchField(field);
        break;
      case 'hex':
        fieldHTML = this.renderHexField(field);
        break;
      case 'textarea':
        fieldHTML = this.renderTextareaField(field);
        break;
      case 'htmlpreview':
        fieldHTML = this.renderHtmlPreviewField(field);
        break;
      case 'json':
        fieldHTML = this.renderJsonField(field);
        break;
      case 'select':
        fieldHTML = this.renderSelectField(field);
        break;
      case 'multiselect':
        fieldHTML = this.renderMultiSelectField(field);
        break;
      case 'checkbox':
        fieldHTML = this.renderCheckboxField(field);
        break;
      case 'toggle':
      case 'switch':
        fieldHTML = this.renderSwitchField(field);
        break;
      case 'radio':
        fieldHTML = this.renderRadioField(field);
        break;
      case 'date':
        fieldHTML = this.renderDateField(field);
        break;
      case 'datetime':
        fieldHTML = this.renderDateTimeField(field);
        break;
      case 'time':
        fieldHTML = this.renderTimeField(field);
        break;
      case 'file':
        fieldHTML = this.renderFileField(field);
        break;
      case 'image':
        fieldHTML = this.renderImageField(field);
        break;
      case 'color':
        fieldHTML = this.renderColorField(field);
        break;
      case 'range':
        fieldHTML = this.renderRangeField(field);
        break;
      case 'hidden':
        fieldHTML = this.renderHiddenField(field);
        break;
      case 'button':
        fieldHTML = this.renderButton(field);
        break;
      case 'divider':
        fieldHTML = this.renderDivider(field);
        break;
      case 'html':
        fieldHTML = this.renderHtmlField(field);
        break;
      case 'heading':
      case 'header':
        fieldHTML = this.renderHeaderField(field);
        break;
      case 'tag':
      case 'tags':
        fieldHTML = this.renderTagField(field);
        break;
      case 'collection':
        fieldHTML = this.renderCollectionField(field);
        break;
      case 'collectionmultiselect':
      case 'collection-multiselect':
        fieldHTML = this.renderCollectionMultiSelectField(field);
        break;
      case 'datepicker':
        fieldHTML = this.renderDatePickerField(field);
        break;
      case 'monthpicker':
        fieldHTML = this.renderDatePickerField({ ...field, precision: 'month' });
        break;
      case 'yearpicker':
        fieldHTML = this.renderDatePickerField({ ...field, precision: 'year' });
        break;
      case 'daterange':
        fieldHTML = this.renderDateRangeField(field);
        break;
      case 'monthrange':
        fieldHTML = this.renderDateRangeField({ ...field, precision: 'month' });
        break;
      case 'yearrange':
        fieldHTML = this.renderDateRangeField({ ...field, precision: 'year' });
        break;
      case 'timepicker':
        fieldHTML = this.renderTimePickerField(field);
        break;
      case 'datetimepicker':
        fieldHTML = this.renderDateTimePickerField(field);
        break;
      case 'checklistdropdown':
        fieldHTML = this.renderChecklistDropdownField(field);
        break;
      case 'buttongroup':
        fieldHTML = this.renderButtonGroupField(field);
        break;
      case 'combo':
      case 'combobox':
      case 'autocomplete':
        fieldHTML = this.renderComboField(field);
        break;
      case 'tabset':
        fieldHTML = this.renderTabsetField(field);
        break;
      default:
        console.warn(`Unknown field type: ${type}`);
        fieldHTML = this.renderTextField(field);
    }
    }

    // Wrap field in column - handle auto-sizing columns and responsive object syntax
    let colClass;
    if (this.isAutoSizingField(field)) {
      colClass = `col ${fieldClass}`.trim();
    } else if (typeof columns === 'object' && columns !== null) {
      // Responsive syntax: columns: { xs: 12, sm: 6, md: 4, lg: 3, xl: 2, xxl: 2 }
      // Emit one Bootstrap class per declared breakpoint; xs maps to bare col-N.
      const parts = [];
      if (columns.xs)  parts.push(`col-${columns.xs}`);
      if (columns.sm)  parts.push(`col-sm-${columns.sm}`);
      if (columns.md)  parts.push(`col-md-${columns.md}`);
      if (columns.lg)  parts.push(`col-lg-${columns.lg}`);
      if (columns.xl)  parts.push(`col-xl-${columns.xl}`);
      if (columns.xxl) parts.push(`col-xxl-${columns.xxl}`);
      if (parts.length === 0) parts.push('col-12');
      if (fieldClass) parts.push(fieldClass);
      colClass = parts.join(' ');
    } else {
      colClass = `col-${columns} ${fieldClass}`.trim();
    }

    // showWhen: conditionally hide field based on another field's value
    let showWhenAttrs = '';
    let showWhenStyle = '';
    if (field.showWhen) {
      const sw = field.showWhen;
      const swValues = Array.isArray(sw.value) ? sw.value : [sw.value];
      showWhenAttrs = ` data-show-when-field="${sw.field}" data-show-when-value="${swValues.join(',')}"`;
      if (sw.negate) {
        showWhenAttrs += ' data-show-when-negate="true"';
      }
      // Determine initial visibility from form data or field default values
      let resolvedValue = this.data[sw.field];
      if (resolvedValue === undefined || resolvedValue === null) {
        // Fall back to the controlling field's default value
        const controlField = this._findField(sw.field, this.fields);
        if (controlField) resolvedValue = controlField.value;
      }
      const currentValue = String(resolvedValue ?? '');
      const matches = swValues.map(String).includes(currentValue);
      const visible = sw.negate ? !matches : matches;
      if (!visible) {
        showWhenStyle = ' style="display:none"';
      }
    }

    return `<div class="${colClass}"${showWhenAttrs}${showWhenStyle}>${fieldHTML}</div>`;
  }

  /**
   * Find a field config by name, searching nested groups and tabsets
   */
  _findField(name, fields) {
    for (const f of fields) {
      if (f.name === name) return f;
      if (f.fields) {
        const found = this._findField(name, f.fields);
        if (found) return found;
      }
      if (f.tabs) {
        for (const tab of f.tabs) {
          if (tab.fields) {
            const found = this._findField(name, tab.fields);
            if (found) return found;
          }
        }
      }
    }
    return null;
  }

  /**
   * Generate field ID
   * @param {string} name - Field name
   * @returns {string} Unique field ID
   */
  getFieldId(name) {
    // Replace dots, spaces, and special characters with underscores for valid HTML IDs
    if (!name) {
        return `field_${Math.random().toString(36).substr(2, 9)}`;
    }
    const safeName = name.replace(/[.\s\[\]]/g, '_');
    return `field_${safeName}`;
  }

  /**
   * Render text input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderTextField(field) {
    return this.renderInputField(field, 'text');
  }

  /**
   * Render email input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderEmailField(field) {
    return this.renderInputField(field, 'email');
  }

  /**
   * Render password input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderPasswordField(field) {
    const passwordUsage = field.passwordUsage || 'current';
    const inferredAutocomplete =
      passwordUsage === 'new' || passwordUsage === 'new-password'
        ? 'new-password'
        : 'current-password';

    const attributes = {
      ...(field.attributes || {}),
      autocomplete: (field.attributes && field.attributes.autocomplete) || inferredAutocomplete
    };

    return this.renderInputField(
      {
        ...field,
        showToggle: field.showToggle !== false, // default to showing the toggle unless explicitly disabled
        attributes
      },
      'password'
    );
  }

  /**
   * Render number input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderNumberField(field) {
    const {
      min,
      max,
      step = 1,
      ...baseField
    } = field;

    const attrs = [];
    if (min !== undefined) attrs.push(`min="${min}"`);
    if (max !== undefined) attrs.push(`max="${max}"`);
    if (step !== undefined) attrs.push(`step="${step}"`);

    return this.renderInputField({
      ...baseField,
      attributes: { ...baseField.attributes, ...attrs.reduce((obj, attr) => {
        const [key, value] = attr.split('=');
        obj[key] = value.replace(/"/g, '');
        return obj;
      }, {}) }
    }, 'number');
  }

  /**
   * Render tel input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderTelField(field) {
    return this.renderInputField(field, 'tel');
  }

  /**
   * Render URL input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderUrlField(field) {
    return this.renderInputField(field, 'url');
  }

  /**
   * Render search input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderSearchField(field) {
    const searchField = {
      ...field,
      attributes: {
        'data-filter': 'live-search',
        'data-change-action': 'filter-search',
        'data-filter-debounce': field.debounce || '300',
        ...field.attributes
      }
    };
    return this.renderInputField(searchField, 'search');
  }

  /**
   * Render hex input field with validation
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderHexField(field) {
    const {
      hexType = 'color', // 'color', 'string', 'any'
      allowPrefix = true,
      minLength: originalMinLength,
      maxLength: originalMaxLength,
      ...baseField
    } = field;

    let pattern, minLength, maxLength, placeholder, help;

    switch (hexType) {
      case 'color':
        // Hex color: #RRGGBB or RRGGBB
        pattern = allowPrefix ? '^#?[0-9A-Fa-f]{6}$' : '^[0-9A-Fa-f]{6}$';
        minLength = allowPrefix ? 6 : 6;
        maxLength = allowPrefix ? 7 : 6;
        placeholder = allowPrefix ? '#FF0000' : 'FF0000';
        help = help || 'Enter a valid hex color (e.g., ' + placeholder + ')';
        break;

      case 'color-short':
        // Hex color: #RGB or RGB, #RRGGBB or RRGGBB
        pattern = allowPrefix ? '^#?[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$' : '^[0-9A-Fa-f]{3}([0-9A-Fa-f]{3})?$';
        minLength = allowPrefix ? 4 : 3;
        maxLength = allowPrefix ? 7 : 6;
        placeholder = allowPrefix ? '#F00 or #FF0000' : 'F00 or FF0000';
        help = help || 'Enter a valid hex color (3 or 6 digits)';
        break;

      case 'string':
        // General hex string
        pattern = '^[0-9A-Fa-f]+$';
        minLength = originalMinLength || 1;
        maxLength = originalMaxLength || 64;
        placeholder = 'ABCDEF123456';
        help = help || 'Only hexadecimal characters (0-9, A-F) allowed';
        break;

      default:
        // Any hex format
        pattern = allowPrefix ? '^#?[0-9A-Fa-f]+$' : '^[0-9A-Fa-f]+$';
        minLength = originalMinLength || 1;
        maxLength = originalMaxLength || 64;
        placeholder = allowPrefix ? '#ABCDEF or ABCDEF' : 'ABCDEF';
        help = help || 'Enter hexadecimal characters only';
    }

    const hexField = {
      ...baseField,
      pattern,
      minLength,
      maxLength,
      placeholder: baseField.placeholder || placeholder,
      help: baseField.help || help,
      attributes: {
        'data-hex-type': hexType,
        'data-allow-prefix': allowPrefix,
        'style': 'text-transform: uppercase;',
        ...baseField.attributes
      }
    };

    return this.renderInputField(hexField, 'text');
  }

  /**
   * Render generic input field
   * @param {Object} field - Field configuration
   * @param {string} type - Input type
   * @returns {string} Field HTML
   */
  renderInputField(field, type = 'text') {
    const {
      name,
      label,
      value = '',
      placeholder = '',
      required = false,
      disabled = false,
      readonly = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      type,
      fieldValue,
      label: label || null,
      placeholder: placeholder || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      required,
      disabled,
      readonly,
      attrs,
      showCopy: !!field.showCopy
    };

    if (type === 'password' && (field.showToggle || field.strengthMeter || field.capsLockWarning)) {
      const enhancedContext = {
        ...context,
        showToggle: !!field.showToggle,
        strengthMeter: !!field.strengthMeter,
        capsLockWarning: !!field.capsLockWarning
      };
      return Mustache.render(this.templates.password, enhancedContext);
    }
    if (type === 'password') {
      const enhancedContext = {
        ...context,
        showToggle: field.showToggle !== false,
        strengthMeter: !!field.strengthMeter,
        capsLockWarning: !!field.capsLockWarning
      };
      return Mustache.render(this.templates.password, enhancedContext);
    }
    return Mustache.render(this.templates.input, context);
  }

  /**
   * Render textarea field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderTextareaField(field) {
    const {
      name,
      label,
      value = '',
      placeholder = '',
      required = false,
      disabled = false,
      readonly = false,
      rows = 3,
      cols: _cols,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      fieldValue: fieldValue, // Pass raw value to template for correct rendering
      label: label || null,
      placeholder: placeholder || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      rows: rows || 3,
      required,
      disabled,
      readonly,
      showCopy: !!field.showCopy,
      attrs
    };

    return Mustache.render(this.templates.textarea, context);
  }

  /**
   * Render HTML preview field (textarea with preview button)
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderHtmlPreviewField(field) {
    const {
      name,
      label,
      value = '',
      placeholder = '',
      required = false,
      disabled = false,
      readonly = false,
      rows = 5,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      fieldValue: fieldValue, // Pass raw value to template for correct rendering
      label: label || null,
      placeholder: placeholder || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      rows: rows || 5,
      required,
      disabled,
      readonly,
      attrs,
      showCopy: !!field.showCopy
    };

    return Mustache.render(this.templates.htmlpreview, context);
  }

  /**
   * Render JSON field as a textarea
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderJsonField(field) {
    const {
      name,
      label,
      placeholder = '',
      required = false,
      disabled = false,
      readonly = false,
      rows = 3,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const rawValue = this.getFieldValue(field.name) ?? field.value ?? {};
    let formattedValue = rawValue;

    if (typeof rawValue === 'object' && rawValue !== null) {
        try {
            formattedValue = JSON.stringify(rawValue, null, 2);
        } catch (e) {
            formattedValue = "{}";
        }
    } else if (typeof rawValue !== 'string') {
        formattedValue = String(rawValue);
    }

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldId = this.getFieldId(name);

    const attrs = Object.entries({
      ...attributes,
      'data-field-type': 'json'
    }).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      fieldValue: formattedValue, // Use the formatted JSON string directly
      label: label || null,
      placeholder: placeholder || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      rows: rows || 3,
      required,
      disabled,
      readonly,
      attrs
    };

    return Mustache.render(this.templates.textarea, context);
  }



  /**
   * Render select field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderSelectField(field) {
    const {
      name,
      label,
      options = [],
      value = '',
      required = false,
      disabled = false,
      multiple = false,
      searchable = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || '',
      // Auto-generation parameters
      start = field.start,
      end = field.end,
      step = field.step,
      format = field.format,
      prefix = field.prefix,
      suffix = field.suffix
    } = field;

    const inputClass = `form-select ${fieldClass}`.trim();
    const error = this.errors[name];
    const dataValue = this.getFieldValue(name);
    const fieldValue = (dataValue ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    // Generate options automatically if start/end are provided
    let generatedOptions = [...options];
    if (start !== undefined && end !== undefined) {
      const stepValue = step !== undefined ? step : 1;
      const autoOptions = this.generateSelectOptions(start, end, stepValue, { format, prefix, suffix });
      // Append auto-generated options after any manually specified ones
      generatedOptions = [...generatedOptions, ...autoOptions];
    }

    let optionsHTML = '';
    if (Array.isArray(generatedOptions)) {
      optionsHTML = generatedOptions.map(option => {
        if (typeof option === 'string') {
          const selected = option === fieldValue ? 'selected' : '';
          return `<option value="${this.escapeHtml(option)}" ${selected}>${this.escapeHtml(option)}</option>`;
        } else if (option && typeof option === 'object') {
          // Use loose equality (==) to handle string/number type coercion
          const selected = option.value == fieldValue ? 'selected' : '';
          return `<option value="${this.escapeHtml(option.value)}" ${selected}>${this.escapeHtml(option.label || option.text || option.value)}</option>`;
        }
        return '';
      }).join('');
    }

    const searchInput = searchable ? `
      <input type="text"
             class="form-control form-control-sm mb-2"
             placeholder="Search options..."
             data-filter="live-search"
             data-change-action="filter-select-options"
             data-target="${fieldId}">
    ` : '';

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      searchInput: searchable ? searchInput : null,
      optionsHTML,
      required,
      disabled,
      multiple,
      attrs
    };

    return Mustache.render(this.templates.select, context);
  }

  /**
   * Render multiselect dropdown field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderMultiSelectField(field) {
    const {
      name,
      label,
      options = [],
      value = [],
      required = false,
      disabled = false,
      maxHeight = 300,
      help = field.helpText || field.help || ''
    } = field;
    
    // Support both placeholder and placeHolder (capital H)
    const placeholder = field.placeholder || field.placeHolder || 'Select...';

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    // Prioritize field.value (which may be an array set by buildFilterDialogField) over data lookup
    const fieldValue = field.value ?? this.getFieldValue(name) ?? value;

    // Create placeholder div that will be replaced with MultiSelectDropdown component
    // Note: MultiSelectDropdown renders its own mojo-form-control wrapper and label,
    // so we don't need to wrap it here like other field types
    return `
      <div class="multiselect-placeholder"
           data-field-name="${name}"
           data-field-type="multiselect"
           data-field-config='${JSON.stringify({
             name,
             value: fieldValue,
             placeholder,
             maxHeight,
             disabled,
             required
           })}'>
        <input type="hidden" name="${name}" value="${this.escapeHtml(JSON.stringify(fieldValue))}">
        <select class="form-select${error ? ' is-invalid' : ''}" 
                multiple 
                ${disabled ? 'disabled' : ''}
                ${required ? 'required' : ''}>
          ${options.map(opt => {
            const optValue = typeof opt === 'string' ? opt : opt.value;
            const optLabel = typeof opt === 'string' ? opt : (opt.label || opt.value);
            const selected = Array.isArray(fieldValue) && fieldValue.includes(optValue) ? 'selected' : '';
            return `<option value="${this.escapeHtml(optValue)}" ${selected}>${this.escapeHtml(optLabel)}</option>`;
          }).join('')}
        </select>
        <small class="form-text text-muted">This will be enhanced with MultiSelectDropdown component</small>
      </div>
    `;
  }

  /**
   * Render checkbox field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderCheckboxField(field) {
    const {
      name,
      label,
      value = false,
      required = false,
      disabled = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);
    const checked = fieldValue === true || fieldValue === 'true' || fieldValue === '1';

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    // Pass raw text for the Mustache `{{…}}` placeholders — Mustache already
    // HTML-escapes them, so pre-escaping here double-escapes (e.g. a label
    // "Verification & Compliance" rendered as "Verification &amp; Compliance").
    const context = {
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      value,
      fieldClass,
      checked,
      required,
      disabled,
      attrs
    };

    return Mustache.render(this.templates.checkbox, context);
  }

  /**
   * Render switch field (enhanced checkbox)
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderSwitchField(field) {
    const {
      name,
      label,
      value = false,
      required = false,
      disabled = false,
      size = 'md',
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);
    const checked = fieldValue === true || fieldValue === 'true' || fieldValue === '1';

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);
    const sizeClass = size !== 'md' ? `form-switch-${size}` : '';

    // Pass raw text for the Mustache `{{…}}` placeholders — Mustache already
    // HTML-escapes them, so pre-escaping here double-escapes (e.g. a label
    // "Verification & Compliance" rendered as "Verification &amp; Compliance").
    const context = {
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      value,
      sizeClass,
      fieldClass,
      checked,
      required,
      disabled,
      attrs
    };

    return Mustache.render(this.templates.switch, context);
  }

  /**
   * Render radio field group
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderRadioField(field) {
    const {
      name,
      label,
      options = [],
      value = '',
      disabled = false,
      inline = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');

    let optionsHTML = '';
    if (Array.isArray(options)) {
      optionsHTML = options.map((option, index) => {
        const radioId = `${name}_${index}`;
        const radioValue = typeof option === 'string' ? option : option.value;
        const radioLabel = typeof option === 'string' ? option : option.label || option.text || option.value;
        const checked = radioValue === fieldValue ? 'checked' : '';
        const inlineClass = inline ? 'form-check-inline' : '';

        return `
          <div class="form-check ${inlineClass}">
            <input
              type="radio"
              id="${radioId}"
              name="${name}"
              class="form-check-input ${error ? 'is-invalid' : ''}"
              value="${this.escapeHtml(radioValue)}"
              ${checked}
              ${disabled ? 'disabled' : ''}

              ${attrs}
            >
            <label class="form-check-label" for="${radioId}">
              ${this.escapeHtml(radioLabel)}
            </label>
          </div>
        `;
      }).join('');
    }

    return `
      <div class="mojo-form-control">
        ${label ? `<fieldset>
          <legend class="${this.options.labelClass}">${this.escapeHtml(label)}${this.renderTooltipIcon(field)}</legend>
          <div class="${fieldClass}">
            ${optionsHTML}
          </div>
        </fieldset>` : `<div class="${fieldClass}">${optionsHTML}</div>`}
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render date field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderDateField(field) {
    return this.renderInputField(field, 'date');
  }

  /**
   * Render datetime field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderDateTimeField(field) {
    return this.renderInputField(field, 'datetime-local');
  }

  /**
   * Render time field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderTimeField(field) {
    return this.renderInputField(field, 'time');
  }

  /**
   * Render file field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderFileField(field) {
    const {
      name,
      label,
      required = false,
      disabled = false,
      multiple = false,
      accept = '*/*',
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      accept,
      required,
      disabled,
      multiple,
      attrs
    };

    return Mustache.render(this.templates.file, context);
  }

  /**
   * Render enhanced image field with drag & drop support
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderImageField(field) {
    const {
      name,
      label,
      required = false,
      disabled = false,
      accept = 'image/*',
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || '',
      size = 'md',
      allowDrop = true,
      placeholder = 'Drop image here or click to upload'
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldId = this.getFieldId(name);
    const dropZoneId = `${fieldId}_dropzone`;
    const previewId = `${fieldId}_preview`;

    // Size configurations
    const sizeMap = {
      xs: { width: 48, height: 48, containerClass: 'image-field-xs' },
      sm: { width: 96, height: 96, containerClass: 'image-field-sm' },
      md: { width: 150, height: 150, containerClass: 'image-field-md' },
      lg: { width: 200, height: 200, containerClass: 'image-field-lg' },
      xl: { width: 300, height: 300, containerClass: 'image-field-xl' }
    };

    const sizeConfig = sizeMap[size] || sizeMap.md;
    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');

    // Get current value (could be URL string or file object)
    const currentValue = this.getFieldValue(name);
    const imageUrl = this.extractImageUrl(currentValue, size);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      dropZoneId,
      previewId,
      containerClass: sizeConfig.containerClass,
      width: sizeConfig.width,
      height: sizeConfig.height,
      accept,
      imageUrl,
      placeholderText: disabled ? 'No image' : (placeholder || ''),
      cursor: disabled ? 'default' : 'pointer',
      allowDrop,
      showRemove: !disabled,
      required,
      disabled,
      attrs
    };

    return Mustache.render(this.templates.image, context);
  }

  /**
   * Extract image URL from field value (handles both URL strings and file objects)
   * @param {string|Object} value - Field value
   * @param {string} size - Requested size (xs, sm, md, lg, xl)
   * @returns {string|null} Image URL or null
   */
  extractImageUrl(value, size = 'md') {
    if (!value) return null;

    // If it's already a URL string
    if (typeof value === 'string') {
      return value;
    }

    // If it's a file object with renditions
    if (typeof value === 'object' && value.url) {
      // Try to get appropriate rendition based on size
      if (value.renditions) {
        const sizeMap = {
          xs: ['thumbnail_sm', 'thumbnail', 'square_sm'],
          sm: ['thumbnail', 'thumbnail_sm', 'square_sm'],
          md: ['thumbnail_md', 'thumbnail', 'thumbnail_lg'],
          lg: ['thumbnail_lg', 'thumbnail_md', 'thumbnail'],
          xl: ['original', 'thumbnail_lg']
        };

        const preferredSizes = sizeMap[size] || sizeMap.md;

        for (const renditionName of preferredSizes) {
          if (value.renditions[renditionName] && value.renditions[renditionName].url) {
            return value.renditions[renditionName].url;
          }
        }
      }

      // Fall back to original URL
      return value.url;
    }

    return null;
  }

  /**
   * Render color field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderColorField(field) {
    const {
      name,
      label,
      value = '',
      placeholder = '',
      required = false,
      disabled = false,
      readonly = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      fieldValue,
      label: label || null,
      placeholder: placeholder || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      required,
      disabled,
      readonly,
      attrs
    };

    return Mustache.render(this.templates.color, context);
  }

  /**
   * Render range field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderRangeField(field) {
    const {
      name,
      label,
      min = 0,
      max = 100,
      step = 1,
      value = min,
      disabled = false,
      class: fieldClass = '',
      attributes = {},
      help = field.helpText || field.help || ''
    } = field;

    const inputClass = `${this.options.inputClass} ${fieldClass}`.trim();
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');
    const fieldId = this.getFieldId(name);

    const context = {
      labelClass: this.options.labelClass,
      inputClass: inputClass,
      helpClass: this.options.helpClass,
      errorClass: this.options.errorClass,
      fieldId,
      name,
      label: label || null,
      help: help || null,
      tooltip: field.tooltip || null,
      error: error || null,
      min,
      max,
      step,
      fieldValue,
      disabled,
      attrs
    };

    return Mustache.render(this.templates.range, context);
  }

  /**
   * Render hidden field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderHiddenField(field) {
    const { name, value = '' } = field;
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `<input type="hidden" name="${name}" value="${this.escapeHtml(fieldValue)}">`;
  }

  /**
   * Render button field
   * @param {Object} field - Field configuration
   * @returns {string} Button HTML
   */
  renderButton(field) {
    const {
      name = '',
      label = 'Button',
      type = 'button',
      action = '',
      class: fieldClass = 'btn-secondary',
      disabled = false,
      attributes = {}
    } = field;

    // Auto-assign actions for submit/reset types
    let buttonAction = action;
    if (!buttonAction) {
      if (type === 'submit') {
        buttonAction = 'submit-form';
      } else if (type === 'reset') {
        buttonAction = 'reset-form';
      }
    }

    const attrs = Object.entries(attributes).map(([key, val]) => `${key}="${this.escapeHtml(val)}"`).join(' ');

    return `
      <button
        type="button"
        ${name ? `name="${name}"` : ''}
        class="btn ${fieldClass}"
        ${buttonAction ? `data-action="${buttonAction}"` : ''}
        ${disabled ? 'disabled' : ''}
        ${attrs}
      >
        ${this.escapeHtml(label)}
      </button>
    `;
  }

  /**
   * Render divider field
   * @param {Object} field - Field configuration
   * @returns {string} Divider HTML
   */
  renderDivider(field) {
    const { label = '', class: fieldClass = '' } = field;

    return `
      <div class="form-divider ${fieldClass}">
        <hr>
        ${label ? `<div class="form-divider-label">${this.escapeHtml(label)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render HTML field
   * @param {Object} field - Field configuration
   * @returns {string} HTML content
   */
  renderHtmlField(field) {
    const { html = '', class: fieldClass = '' } = field;

    return `
      <div class="form-html ${fieldClass}">
        ${html}
      </div>
    `;
  }

  /**
   * Render header field
   * @param {Object} field - Field configuration
   * @returns {string} Header HTML
   */
  renderHeaderField(field) {
    const {
      text = '',
      level = 3,
      class: fieldClass = '',
      id = ''
    } = field;

    const headerLevel = Math.max(1, Math.min(6, parseInt(level)));
    const headerId = id ? ` id="${this.escapeHtml(id)}"` : '';
    const headerClass = fieldClass ? ` class="${this.escapeHtml(fieldClass)}"` : '';

    return `<h${headerLevel}${headerId}${headerClass}>${this.escapeHtml(text)}</h${headerLevel}>`;
  }

  /**
   * Generate buttons HTML
   * @returns {string} Buttons HTML
   */
  buildButtonsHTML() {
    if (!this.options.submitButton && !this.options.resetButton && !this.buttons.length) {
      return '';
    }

    let buttonsHTML = '';

    // Custom buttons
    this.buttons.forEach(button => {
      buttonsHTML += this.renderButton(button) + ' ';
    });

    // Default submit button
    if (this.options.submitButton) {
      let submitLabel = 'Submit';
      if (typeof this.options.submitButton === 'string') {
        submitLabel = this.options.submitButton;
      } else if (this.options.submitButton === true) {
        submitLabel = 'Submit';
      }
      buttonsHTML += `<button type="submit" class="btn btn-primary me-2" data-action="submit-form">${submitLabel}</button>`;
    }

    // Default reset button
    if (this.options.resetButton) {
      let resetLabel = 'Reset';
      if (typeof this.options.resetButton === 'string') {
        resetLabel = this.options.resetButton;
      } else if (this.options.resetButton === true) {
        resetLabel = 'Reset';
      }
      buttonsHTML += `<button type="button" class="btn btn-secondary" data-action="reset-form">${resetLabel}</button>`;
    }

    return buttonsHTML ? `
      <div class="form-actions mt-3">
        ${buttonsHTML}
      </div>
    ` : '';
  }

  /**
   * Get field value from data
   * @param {string} name - Field name
   * @returns {*} Field value
   */
  getFieldValue(name) {
    // If in structure-only mode, return empty value
    if (this.structureOnly) {
      return '';
    }
    const value = MOJOUtils.getContextData(this.data, name);
    return value;
  }

  /**
   * Render tag input field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderTagField(field) {
    const {
      name,
      label,
      value = '',
      placeholder = 'Add tags...',
      required = false,
      disabled = false,
      readonly = false,
      maxTags = 50,
      allowDuplicates = false,
      separator = ',',
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="tag-input-placeholder"
             data-field-name="${name}"
             data-field-type="tag"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               placeholder,
               maxTags,
               allowDuplicates,
               separator,
               disabled,
               readonly,
               required
             })}'>
          <input type="text"
                 id="${fieldId}"
                 name="${name}_display"
                 class="${this.options.inputClass}${error ? ' is-invalid' : ''}"
                 placeholder="${this.escapeHtml(placeholder)}"
                 ${disabled ? 'disabled' : ''}
                 ${readonly ? 'readonly' : ''}

          <input type="hidden" name="${name}" value="${this.escapeHtml(fieldValue)}">
          <small class="form-text text-muted">This will be enhanced with TagInput component</small>
        </div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render collection select field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderCollectionField(field) {
    const {
      name,
      label,
      value = '',
      placeholder = 'Search...',
      required = false,
      disabled = false,
      readonly = false,
      Collection: _Collection,
      labelField = 'name',
      valueField = 'id',
      maxItems = 10,
      emptyFetch = false,
      debounceMs = 300,
      requiresActiveGroup = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="collection-select-placeholder"
             data-field-name="${name}"
             data-field-type="collection"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               placeholder,
               labelField,
               valueField,
               maxItems,
               emptyFetch,
               debounceMs,
               disabled,
               readonly,
               required,
               requiresActiveGroup
             })}'>
          <input type="text"
                 id="${fieldId}"
                 name="${name}_display"
                 class="${this.options.inputClass}${error ? ' is-invalid' : ''}"
                 placeholder="${this.escapeHtml(placeholder)}"
                 ${disabled ? 'disabled' : ''}
                 ${readonly ? 'readonly' : ''}

          <input type="hidden" name="${name}" value="${this.escapeHtml(fieldValue)}">
          <small class="form-text text-muted">This will be enhanced with CollectionSelect component</small>
        </div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render collection multiselect field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderCollectionMultiSelectField(field) {
    const {
      name,
      label,
      value = [],
      required = false,
      disabled = false,
      Collection: _Collection,
      collectionParams = {},
      labelField = 'name',
      valueField = 'id',
      excludeIds = [],
      ignoreIds = [],
      size = 8,
      maxHeight = null,
      showSelectAll = true,
      enableSearch = false,
      searchPlaceholder = 'Search...',
      searchDebounce = 400,
      requiresActiveGroup = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = this.getFieldValue(name) ?? value;

    return `
      <div class="mojo-form-control">
        ${label ? `<label class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="collection-multiselect-placeholder"
             data-field-name="${name}"
             data-field-type="collectionmultiselect"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               labelField,
               valueField,
               excludeIds,
               ignoreIds,
               size,
               maxHeight,
               showSelectAll,
               enableSearch,
               searchPlaceholder,
               searchDebounce,
               disabled,
               required,
               requiresActiveGroup
             })}'>
          <input type="hidden" name="${name}" value="${this.escapeHtml(JSON.stringify(fieldValue))}">
          <small class="form-text text-muted">This will be enhanced with CollectionMultiSelect component</small>
        </div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render enhanced date picker field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderDatePickerField(field) {
    const {
      name,
      label,
      value = '',
      precision = 'day',
      placeholder,
      required = false,
      disabled = false,
      readonly = false,
      min = null,
      max = null,
      format = null,
      displayFormat = null,
      disabledDates = [],
      firstDay = 1,
      lang = 'en-US',
      autoApply = true,
      inline = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="date-picker-placeholder"
             data-field-name="${name}"
             data-field-type="datepicker"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               precision,
               placeholder,
               min,
               max,
               format,
               displayFormat,
               disabledDates,
               firstDay,
               lang,
               autoApply,
               inline,
               disabled,
               readonly,
               required
             })}'></div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render date range picker field
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderDateRangeField(field) {
    const {
      name,
      startName,
      endName,
      fieldName,
      label,
      startDate = '',
      endDate = '',
      precision = 'day',
      placeholder,
      required = false,
      disabled = false,
      readonly = false,
      min = null,
      max = null,
      format = null,
      displayFormat = null,
      outputFormat = 'date',
      separator = ' – ',
      months = null,
      presets = null,
      autoApply = true,
      inline = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name || startName || 'daterange');
    const error = this.errors[name];
    const startFieldName = startName || (name ? name + '_start' : '');
    const endFieldName = endName || (name ? name + '_end' : '');
    const startValue = this.getFieldValue(startFieldName) || startDate;
    const endValue = this.getFieldValue(endFieldName) || endDate;

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="date-range-picker-placeholder"
             data-field-name="${name || startName || 'daterange'}"
             data-field-type="daterange"
             data-field-config='${JSON.stringify({
               name,
               startName,
               endName,
               fieldName,
               startDate: startValue,
               endDate: endValue,
               precision,
               placeholder,
               min,
               max,
               format,
               displayFormat,
               outputFormat,
               separator,
               months,
               presets,
               autoApply,
               inline,
               disabled,
               readonly,
               required
             })}'></div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render time picker placeholder. Hydrated by FormView.initializeTimePickers().
   */
  renderTimePickerField(field) {
    const {
      name,
      label,
      value = '',
      format = '24h',
      step = 1,
      min = null,
      max = null,
      placeholder,
      required = false,
      disabled = false,
      readonly = false,
      timezone = false,
      timezones = null,
      outputFormat = 'string',
      autoApply = false,
      inline = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="time-picker-placeholder"
             data-field-name="${name}"
             data-field-type="timepicker"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               format,
               step,
               min,
               max,
               placeholder,
               timezone,
               timezones,
               outputFormat,
               autoApply,
               inline,
               disabled,
               readonly,
               required
             })}'></div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render datetime picker placeholder. Hydrated by FormView.initializeDateTimePickers().
   */
  renderDateTimePickerField(field) {
    const {
      name,
      label,
      value = '',
      displayFormat = null,
      timeFormat = '24h',
      timeStep = 1,
      min = null,
      max = null,
      placeholder,
      required = false,
      disabled = false,
      readonly = false,
      disabledDates = [],
      firstDay = 1,
      lang = 'en-US',
      timezone = false,
      timezones = null,
      outputFormat = 'string',
      autoApply = false,
      inline = false,
      help = field.helpText || field.help || ''
    } = field;

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    const fieldValue = (this.getFieldValue(name) ?? value);

    return `
      <div class="mojo-form-control">
        ${label ? `<label for="${fieldId}" class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="datetime-picker-placeholder"
             data-field-name="${name}"
             data-field-type="datetimepicker"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               displayFormat,
               timeFormat,
               timeStep,
               min,
               max,
               placeholder,
               disabledDates,
               firstDay,
               lang,
               timezone,
               timezones,
               outputFormat,
               autoApply,
               inline,
               disabled,
               readonly,
               required
             })}'></div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render checklistdropdown field
   * @param {Object} field - Field configuration
   * @returns {string} Rendered HTML
   */
  renderChecklistDropdownField(field) {
    const fieldId = this.getFieldId(field.name);
    const selectedValues = (this.getFieldValue(field.name) ?? []);

    // Prepare data for Mustache template
    const templateData = {
      fieldId,
      fieldName: field.name,
      buttonText: field.buttonText || 'Select Options',
      buttonIcon: field.buttonIcon || 'bi-chevron-down',
      buttonClass: field.buttonClass || 'btn btn-outline-secondary btn-sm dropdown-toggle',
      dropdownClass: field.dropdownClass || 'dropdown-menu p-2',
      minWidth: field.minWidth || '200px',
      options: field.options.map(option => ({
        value: option.value,
        label: option.label,
        id: `${field.name}-${option.value}`,
        checked: selectedValues.includes(option.value)
      }))
    };

    return Mustache.render(this.templates.checklistdropdown, templateData);
  }

  /**
   * Render buttongroup field
   * @param {Object} field - Field configuration
   * @returns {string} Rendered HTML
   */
  renderButtonGroupField(field) {
    const fieldId = this.getFieldId(field.name);
    const selectedValue = (this.getFieldValue(field.name) ?? field.value);

    // Prepare data for Mustache template
    const templateData = {
      fieldId,
      fieldName: field.name,
      size: field.size || 'sm',
      variant: field.variant || 'outline-primary',
      options: field.options.map(option => ({
        value: option.value,
        label: option.label,
        action: option.action,
        active: option.value === selectedValue,
        buttonClass: this.getButtonClass(option.value === selectedValue, field.variant)
      }))
    };

    return Mustache.render(this.templates.buttongroup, templateData);
  }

  /**
   * Get button class based on active state and variant
   * @param {boolean} isActive - Whether button is active
   * @param {string} variant - Button variant
   * @returns {string} Button class
   */
  getButtonClass(isActive, variant = 'outline-primary') {
    if (isActive) {
      // Remove 'outline-' prefix for active buttons
      const activeVariant = variant.replace('outline-', '');
      return `btn btn-${activeVariant}`;
    }
    return `btn btn-${variant}`;
  }

  /**
   * Render combo input field (editable select/autocomplete)
   * @param {Object} field - Field configuration
   * @returns {string} Field HTML
   */
  renderComboField(field) {
    const {
      name,
      label,
      value = '',
      required = false,
      disabled = false,
      maxHeight = 300,
      help = field.helpText || field.help || ''
    } = field;
    
    // Support both placeholder and placeHolder (capital H)
    const placeholder = field.placeholder || field.placeHolder || 'Type or select...';
    const allowCustom = field.allowCustom !== false; // Default: true

    const fieldId = this.getFieldId(name);
    const error = this.errors[name];
    // Prioritize field.value over data lookup
    const fieldValue = field.value ?? this.getFieldValue(name) ?? value;

    // Create placeholder div that will be replaced with ComboBox component
    return `
      <div class="mojo-form-control">
        ${label ? `<label class="${this.options.labelClass}">${this.escapeHtml(label)}${required ? '<span class="text-danger">*</span>' : ''}${this.renderTooltipIcon(field)}</label>` : ''}
        <div class="combobox-placeholder"
             data-field-name="${name}"
             data-field-type="combobox"
             data-field-config='${JSON.stringify({
               name,
               value: fieldValue,
               placeholder,
               maxHeight,
               allowCustom,
               disabled,
               required
             })}'>
          <input type="text" 
                 class="form-control${error ? ' is-invalid' : ''}"
                 value="${this.escapeHtml(fieldValue)}"
                 placeholder="${this.escapeHtml(placeholder)}"
                 ${disabled ? 'disabled' : ''}
                 ${required ? 'required' : ''}>
          <small class="form-text text-muted">This will be enhanced with ComboBox component</small>
        </div>
        ${help ? `<div class="${this.options.helpClass}">${this.escapeHtml(help)}</div>` : ''}
        ${error ? `<div class="${this.options.errorClass}">${this.escapeHtml(error)}</div>` : ''}
      </div>
    `;
  }

  /**
   * Render a tabset field consisting of nav tabs and tab panes.
   * Each pane's fields are rendered using existing field rendering,
   * wrapped in a row so column layouts work as expected.
   *
   * The nav uses the same variant system as `TabView` so the look matches
   * standalone TabView components elsewhere in the app. By default the
   * tabset renders with `TabView.DEFAULT_VARIANT` (`'underline-all'`).
   *
   * @param {Object} field - Tabset configuration
   * @param {Array} field.tabs - Array of tabs: [{ label, fields }]
   * @param {string} [field.name] - Optional name used to generate stable IDs
   * @param {string} [field.variant] - TabView variant name (e.g. `'minimal'`,
   *   `'pills'`, `'segmented'`). Resolves through `TabView.VARIANT_CLASSES`.
   *   Defaults to `TabView.DEFAULT_VARIANT`.
   * @param {string} [field.navClass] - Direct CSS classes for the tabs nav.
   *   Wins over `variant` when set.
   * @param {string} [field.contentClass] - CSS classes for the tab content container
   * @returns {string} HTML
   */
  renderTabsetField(field) {
    const {
      tabs = [],
      name = `tabset-${Date.now()}`,
      variant,
      navClass,
      contentClass = 'tab-content'
    } = field;

    // Resolve nav classes: explicit `navClass` wins; otherwise look up
    // the variant in TabView's table. Unknown variants fall back to the
    // default — match TabView's own resolution behavior.
    const resolvedVariant = variant && Object.prototype.hasOwnProperty.call(TabView.VARIANT_CLASSES, variant)
      ? variant
      : TabView.DEFAULT_VARIANT;
    const resolvedNavClass = navClass || TabView.VARIANT_CLASSES[resolvedVariant];

    const safe = String(name).toLowerCase().replace(/[^a-z0-9]/g, '-');

    const nav = tabs.map((t, i) => {
      const id = `${safe}-pane-${i}`;
      const isActive = i === 0;
      return `
        <li class="nav-item" role="presentation">
          <button class="nav-link ${isActive ? 'active' : ''}"
                  id="${id}-tab"
                  data-bs-toggle="tab"
                  data-bs-target="#${id}"
                  type="button"
                  role="tab"
                  aria-controls="${id}"
                  aria-selected="${isActive}">
            ${this.escapeHtml(t.label || `Tab ${i + 1}`)}
          </button>
        </li>
      `;
    }).join('');

    const panes = tabs.map((t, i) => {
      const id = `${safe}-pane-${i}`;
      const isActive = i === 0;
      
      // Handle groups and regular fields properly
      const fieldsHTML = (t.fields || []).map(f => {
        if (f.type === 'group') {
          return this.buildGroupHTML(f);
        }
        return this.buildFieldHTML(f);
      }).join('');
      
      return `
        <div class="tab-pane fade ${isActive ? 'show active' : ''}"
             id="${id}"
             role="tabpanel"
             aria-labelledby="${id}-tab"
             data-tab-index="${i}">
          <div class="row">
            ${fieldsHTML}
          </div>
        </div>
      `;
    }).join('');

    return `
      <div class="mojo-form-tabset">
        <ul class="${resolvedNavClass}" role="tablist">
          ${nav}
        </ul>
        <div class="${contentClass}">
          ${panes}
        </div>
      </div>
    `;
  }

  /**
   * Generate select options automatically from numeric range or patterns
   * Supports multiple generation modes:
   * - Numeric ranges: start, end, step
   * - Formatting: format function, prefix, suffix
   *
   * @param {number} start - Start value (inclusive)
   * @param {number} end - End value (inclusive)
   * @param {number} step - Step increment (default: 1)
   * @param {Object} options - Additional options
   * @param {Function|string} options.format - Format function or preset ('padded', 'ordinal')
   * @param {string} options.prefix - Prefix for label
   * @param {string} options.suffix - Suffix for label
   * @returns {Array} Array of option objects {value, label}
   *
   * @example
   * // Hours 1-24
   * generateSelectOptions(1, 24, 1)
   *
   * // Minutes in 15-min increments with padding
   * generateSelectOptions(0, 45, 15, { format: 'padded' })
   *
   * // Days with ordinal suffix
   * generateSelectOptions(1, 31, 1, { format: 'ordinal' })
   *
   * // Years with prefix
   * generateSelectOptions(2020, 2030, 1, { prefix: 'Year ' })
   *
   * // Percentages
   * generateSelectOptions(0, 100, 10, { suffix: '%' })
   *
   * // Custom formatter
   * generateSelectOptions(1, 12, 1, {
   *   format: (v) => ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
   *                   'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'][v-1]
   * })
   */
  generateSelectOptions(start, end, step = 1, options = {}) {
    const { format, prefix = '', suffix = '' } = options;
    const results = [];

    // Determine direction (ascending or descending)
    const increment = start <= end ? Math.abs(step) : -Math.abs(step);

    // Generate options
    for (let i = start; start <= end ? i <= end : i >= end; i += increment) {
      let label = String(i);

      // Apply formatting
      if (typeof format === 'function') {
        label = format(i);
      } else if (format === 'padded' || format === 'pad') {
        // Pad with zeros to match end value length
        const maxLength = String(Math.max(Math.abs(start), Math.abs(end))).length;
        label = String(i).padStart(maxLength, '0');
      } else if (format === 'ordinal') {
        label = this.formatOrdinal(i);
      }

      // Apply prefix/suffix
      label = `${prefix}${label}${suffix}`;

      results.push({
        value: i,
        label: label
      });
    }

    return results;
  }

  /**
   * Format number with ordinal suffix (1st, 2nd, 3rd, etc.)
   * @param {number} num - Number to format
   * @returns {string} Formatted ordinal string
   */
  formatOrdinal(num) {
    const j = num % 10;
    const k = num % 100;

    if (j === 1 && k !== 11) return num + 'st';
    if (j === 2 && k !== 12) return num + 'nd';
    if (j === 3 && k !== 13) return num + 'rd';
    return num + 'th';
  }

  /**
   * Escape HTML characters to prevent XSS
   * @param {string} str - String to escape
   * @returns {string} Escaped string
   */
  escapeHtml(str) {
    if (str == null) return '';
    // Explicit replacement (not div.textContent/innerHTML) so quotes are
    // escaped too — used in attribute contexts (value=, placeholder=, ...).
    return String(str)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#39;');
  }

  /**
   * Render a tooltip icon for a field
   * @param {Object} field - Field configuration with optional tooltip property
   * @returns {string} HTML for tooltip icon, or empty string
   */
  renderTooltipIcon(field) {
    if (!field || !field.tooltip) return '';
    return ` <i class="bi bi-info-circle text-muted" data-bs-toggle="tooltip" title="${this.escapeHtml(field.tooltip)}" style="font-size: 0.75rem; cursor: help;"></i>`;
  }
}

// Export for use in MOJO framework
export { FormBuilder };
export default FormBuilder;
